import React, { forwardRef, useMemo } from 'react';
import c from 'classnames';
import isFunction from 'lodash/isFunction';

import useForkRef from '../../hooks/useForkRef';
import { usePopoverContext } from '../PopoverContext';
import { PopoverButtonProps } from './types';
import { getAnchorFromChildren } from './utils';

// Reference: https://github.com/uber/baseweb/blob/master/src/popover/popover.js#L295
const PopoverButton = forwardRef<HTMLElement, PopoverButtonProps>((props, forwardedRef) => {
  const { children, classes, className, disabled, stopPropagation = true, ...rest } = props;

  const { onOpenChange, open, setAnchorEl } = usePopoverContext();
  const ref = useForkRef(forwardedRef, setAnchorEl);
  const anchor = getAnchorFromChildren(isFunction(children) ? children({ open }) : children);

  const anchorProps = useMemo(() => {
    const anchorElement = anchor as null | React.ReactElement;

    if (!anchorElement) {
      return {};
    }

    const onClick = (...params: any) => {
      if (stopPropagation) {
        const event = params[0];
        event?.preventDefault?.();
        event?.stopPropagation?.();
      }

      anchorElement.props?.onClick?.(...params);
      onOpenChange(true);
    };

    const anchorClassName = c(
      className,
      classes?.root,
      anchorElement.props?.className,
      disabled && classes?.disabled,
      open ? classes?.open : classes?.closed,
      { 'cursor-not-allowed': disabled, open },
    );

    return {
      'aria-expanded': !!open,
      'aria-haspopup': true,
      className: anchorClassName,
      disabled,
      onClick: disabled ? undefined : onClick,
      ref,
    };
  }, [anchor, className, classes, disabled, onOpenChange, open, ref, stopPropagation]);

  if (!anchor) {
    return null;
  }

  if (typeof anchor === 'object' && React.isValidElement(anchor)) {
    return React.cloneElement(anchor, { ...rest, ...anchorProps });
  }

  return (
    <span {...rest} {...anchorProps}>
      {anchor}
    </span>
  );
});

export { PopoverButtonProps };
export default PopoverButton;
